import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { fetchCommandTypes, sendCommand, type Device } from '@/lib/traccar-api';
import { fetchDeviceStockPaged, fetchSimCard } from '@/lib/reseller-api';

// Command types that require extra attributes
const COMMANDS_WITH_DATA = ['custom', 'setServer'];
const COMMANDS_WITH_FREQUENCY = ['positionPeriodic'];
const COMMANDS_WITH_PORT = ['outputControl'];

// Builds the set-server command string based on device model and APN
const getSetServerCommand = (device: Device, apn: string, gpsServer: string): string => {
  const hostname = gpsServer || `gps.${window.location.hostname}`;
  const model = (device.model || '').toLowerCase();

  if (/^st[0-9]/.test(model)) {
    // Suntech NTW command (Traccar port 9016)
    return `ST300NTW;${device.uniqueId};02;1;${apn || ''},,,${hostname};9016;jsrastreamento.stctecnologia.com.br;9016;;`;
  }

  if (model === 'bn303') {
    // Coban command (Traccar port 5001)
    return `adminip123456 ${hostname} 5001`;
  }

  // GT06 protocol — default (Traccar port 5023)
  return `SERVER,1,${hostname},5023,0#`;
};

// Fallback list if API doesn't return types
const FALLBACK_COMMAND_TYPES = [
  'custom',
  'positionSingle',
  'positionPeriodic',
  'positionStop',
  'engineStop',
  'engineResume',
  'alarmArm',
  'alarmDisarm',
  'requestPhoto',
  'reboot',
  'getVersion',
  'identification',
];

interface SendCommandDialogProps {
  devices: Device[];
  trigger?: React.ReactNode;
}

export function SendCommandDialog({ devices, trigger }: SendCommandDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [commandType, setCommandType] = useState('setServer');
  const [customData, setCustomData] = useState('');
  const [frequency, setFrequency] = useState('');
  const [port, setPort] = useState('1');
  const [portState, setPortState] = useState('1');
  const [useSms, setUseSms] = useState(false);

  const firstDevice = devices[0];
  const isMulti = devices.length > 1;

  const { data: tenantInfo } = useQuery({
    queryKey: ['tenant-info'],
    queryFn: async () => {
      const res = await fetch(`https://raw.githubusercontent.com/rastreosat/rastreosat.github.io/refs/heads/main/${window.location.hostname}/info.json`);
      return res.ok ? res.json() as Promise<{ server?: string }> : {};
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: deviceStockResult } = useQuery({
    queryKey: ['device-stock-by-imei', firstDevice?.uniqueId],
    queryFn: () => fetchDeviceStockPaged({ page: 1, limit: 1, search: firstDevice!.uniqueId }),
    enabled: open && !!firstDevice?.uniqueId,
    staleTime: 60000,
  });

  const simCardId = deviceStockResult?.data[0]?.sim_card_id;

  const { data: simCard } = useQuery({
    queryKey: ['sim-card', simCardId],
    queryFn: () => fetchSimCard(simCardId!),
    enabled: !!simCardId,
    staleTime: 60000,
  });

  const simCardApn = simCard?.apn ?? '';

  const { data: commandTypes = [] } = useQuery({
    queryKey: ['command-types', firstDevice?.id],
    queryFn: () => fetchCommandTypes(firstDevice!.id),
    enabled: open && !!firstDevice,
    staleTime: 60000,
  });

  const availableTypes = [
    'setServer',
    ...(commandTypes.length > 0 ? commandTypes.map(ct => ct.type) : FALLBACK_COMMAND_TYPES).filter(t => t !== 'custom'),
  ];

  const gpsServer = tenantInfo?.server ?? '';

  // Autofill textarea when setServer is selected, APN or server resolves; clear when switching away
  useEffect(() => {
    if (!open) return;
    if (commandType === 'setServer' && firstDevice) {
      if (isMulti) {
        setCustomData(t('devices.setServerMultiHint', { count: devices.length }));
      } else {
        setCustomData(getSetServerCommand(firstDevice, simCardApn, gpsServer));
      }
    } else {
      setCustomData('');
    }
  }, [open, commandType, simCardApn, gpsServer, isMulti, devices.length, firstDevice]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setCommandType('setServer');
      setCustomData('');
      setFrequency('');
      setPort('1');
      setPortState('1');
      setUseSms(false);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const sendType = commandType === 'setServer' ? 'custom' : commandType;
      return Promise.all(devices.map(device => {
        const attributes: Record<string, unknown> = {};
        if (COMMANDS_WITH_DATA.includes(commandType)) {
          // For setServer, generate the command per device (model-specific)
          attributes.data = commandType === 'setServer'
            ? (isMulti ? getSetServerCommand(device, simCardApn, gpsServer) : customData)
            : customData;
        }
        if (COMMANDS_WITH_FREQUENCY.includes(commandType) && frequency) attributes.frequency = Number(frequency);
        if (COMMANDS_WITH_PORT.includes(commandType)) {
          attributes.index = Number(port);
          attributes.data = portState;
        }
        return sendCommand({ deviceId: device.id, type: sendType, textChannel: useSms || undefined, attributes });
      }));
    },
    onSuccess: () => {
      toast.success(isMulti
        ? t('devices.commandSentMulti', { count: devices.length })
        : t('devices.commandSent'));
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('devices.commandError'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commandType) return;
    mutation.mutate();
  };

  if (!firstDevice) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Terminal className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Terminal className="h-4 w-4" />
              {t('devices.sendCommand')} —{' '}
              {isMulti
                ? t('devices.selectedCount', { count: devices.length })
                : firstDevice.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('devices.commandType')}</Label>
              <Select value={commandType} onValueChange={setCommandType}>
                <SelectTrigger>
                  <SelectValue placeholder={t('devices.selectCommandType')} />
                </SelectTrigger>
                <SelectContent>
                  {availableTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type === 'setServer'
                        ? t('devices.preset_setServer')
                        : t(`devices.command_${type}`, { defaultValue: type })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {COMMANDS_WITH_DATA.includes(commandType) && (
              <div className="grid gap-2">
                <Label htmlFor="cmd-data">{t('devices.commandData')}</Label>
                <Textarea
                  id="cmd-data"
                  value={customData}
                  onChange={(e) => setCustomData(e.target.value)}
                  placeholder={t('devices.commandDataPlaceholder')}
                  rows={isMulti && commandType === 'setServer' ? 2 : 4}
                  className="font-mono text-sm"
                  readOnly={isMulti}
                />
                {isMulti && commandType === 'setServer' && (
                  <p className="text-xs text-muted-foreground">{t('devices.setServerMultiNote')}</p>
                )}
              </div>
            )}

            {COMMANDS_WITH_FREQUENCY.includes(commandType) && (
              <div className="grid gap-2">
                <Label htmlFor="cmd-frequency">{t('devices.commandFrequency')}</Label>
                <div className="relative">
                  <Input
                    id="cmd-frequency"
                    type="number"
                    min="1"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    placeholder="30"
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">s</span>
                </div>
              </div>
            )}

            {COMMANDS_WITH_PORT.includes(commandType) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cmd-port">{t('devices.commandPort')}</Label>
                  <Input
                    id="cmd-port"
                    type="number"
                    min="1"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>{t('devices.commandPortState')}</Label>
                  <Select value={portState} onValueChange={setPortState}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t('common.on')}</SelectItem>
                      <SelectItem value="0">{t('common.off')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch id="sms-toggle" checked={useSms} onCheckedChange={setUseSms} />
            <Label htmlFor="sms-toggle">{t('devices.sendViaSms')}</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('devices.sendCommand')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
